// frontend/src/pages/Reinsurance/components/ContractManagement/ContractList/index.js

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, faSearch, faFilter, 
  faExclamationCircle, faSync, faTimes,
  faUndo
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { reinsuranceService } from '../../../../../services/reinsurance';
import ReinsuranceTable from '../../common/ReinsuranceTable';
import Dropdown from '../../../../../components/form/Dropdown/Dropdown';
import styles from './ContractList.module.css';

const ContractList = ({ onViewContract, onCreateContract }) => {
  const { t } = useLanguage();
  const [contracts, setContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'contract_number', direction: 'ascending' });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    contractType: '',
    contractStructure: ''
  });
  
  // Prepare dropdown options
  const statusOptions = [
    { value: '', labelKey: 'all_statuses' },
    ...reinsuranceService.getContractStatusOptions().map(option => ({
      value: option.value,
      labelKey: option.value // Using the value as the translation key
    }))
  ];
  
  const contractTypeOptions = [
    { value: '', labelKey: 'all_types' },
    ...reinsuranceService.getContractTypeOptions().map(option => ({
      value: option.value,
      labelKey: option.value // Using the value as the translation key
    }))
  ];
  
  const contractStructureOptions = [
    { value: '', labelKey: 'all_structures' },
    ...reinsuranceService.getContractStructureOptions().map(option => ({
      value: option.value,
      labelKey: option.value // Using the value as the translation key
    }))
  ];
  
  // Fetch contracts
  const fetchContracts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Prepare API filters (only include those supported by the API)
      const apiFilters = {};
      if (filters.status) apiFilters.status = filters.status;
      if (filters.contractType) apiFilters.contractType = filters.contractType;
      
      const response = await reinsuranceService.listContracts(apiFilters);
      
      if (response && response.success) {
        setContracts(response.data || []);
        setFilteredContracts(response.data || []);
      } else {
        throw new Error(response?.message || 'Failed to load contracts');
      }
    } catch (err) {
      console.error('Error fetching contracts:', err);
      setError(err.message || 'Failed to load contracts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [filters.status, filters.contractType]);
  
  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);
  
  // Apply search, client-side filtering, and sort
  useEffect(() => {
    if (contracts.length > 0) {
      let filtered = [...contracts];
      
      // Apply search
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(contract => 
          (contract.contract_number && contract.contract_number.toLowerCase().includes(search)) ||
          (contract.rnr && contract.rnr.toLowerCase().includes(search)) ||
          (contract.cedent && contract.cedent.cedent_name && contract.cedent.cedent_name.toLowerCase().includes(search)) ||
          (contract.contract_type && contract.contract_type.toLowerCase().includes(search))
        );
      }
      
      // Apply client-side structure filter (since it's not supported by the API)
      if (filters.contractStructure) {
        filtered = filtered.filter(contract => 
          contract.contract_structure === filters.contractStructure
        );
      }
      
      // Apply sorting
      if (sortConfig.key) {
        filtered.sort((a, b) => {
          let aValue, bValue;
          
          // Handle nested properties
          if (sortConfig.key === 'cedent_name') {
            aValue = a.cedent?.cedent_name || '';
            bValue = b.cedent?.cedent_name || '';
          } else if (sortConfig.key === 'effective_date') {
            aValue = a.terms?.effective_date || '';
            bValue = b.terms?.effective_date || '';
          } else if (sortConfig.key === 'expiration_date') {
            aValue = a.terms?.expiration_date || '';
            bValue = b.terms?.expiration_date || '';
          } else if (sortConfig.key === 'limit') {
            aValue = a.terms?.limit || 0;
            bValue = b.terms?.limit || 0;
          } else if (sortConfig.key === 'premium') {
            aValue = a.terms?.premium?.base_amount || 0;
            bValue = b.terms?.premium?.base_amount || 0;
          } else if (sortConfig.key === 'status') {
            aValue = a.performance?.current_status || '';
            bValue = b.performance?.current_status || '';
          } else {
            aValue = a[sortConfig.key] || '';
            bValue = b[sortConfig.key] || '';
          }
          
          if (aValue < bValue) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }
          return 0;
        });
      }
      
      setFilteredContracts(filtered);
    }
  }, [contracts, searchTerm, filters.contractStructure, sortConfig]);
  
  // Handle sort
  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };
  
  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle filter change
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  // Reset filters
  const resetFilters = () => {
    setFilters({
      status: '',
      contractType: '',
      contractStructure: ''
    });
    setSearchTerm('');
  };
  
  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };
  
  // Check if any filters are active
  const hasActiveFilters = () => {
    return filters.status !== '' || filters.contractType !== '' || filters.contractStructure !== '' || searchTerm !== '';
  };
  
  // Table columns
  const columns = [
    { id: 'contract_number', label: 'contract_number', sortable: true },
    { id: 'rnr', label: 'rnr', sortable: true },
    { id: 'cedent_name', label: 'cedent', accessor: (row) => row.cedent?.cedent_name || '', sortable: true },
    { id: 'contract_type', label: 'type', sortable: true },
    { id: 'contract_structure', label: 'structure', sortable: true },
    { id: 'effective_date', label: 'effective_date', accessor: (row) => row.terms?.effective_date || '', format: 'date', sortable: true },
    { id: 'expiration_date', label: 'expiration_date', accessor: (row) => row.terms?.expiration_date || '', format: 'date', sortable: true },
    { id: 'limit', label: 'limit', accessor: (row) => row.terms?.limit || 0, format: 'currency', sortable: true },
    { id: 'premium', label: 'premium', accessor: (row) => row.terms?.premium?.base_amount || 0, format: 'currency', sortable: true },
    { id: 'status', label: 'status', accessor: (row) => row.performance?.current_status || '', sortable: true }
  ];
  
  if (error) {
    return (
      <div className={styles.error}>
        <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
        <p>{error}</p>
        <button className={styles.retryButton} onClick={fetchContracts}>
          <FontAwesomeIcon icon={faSync} />
          <span>{t('retry')}</span>
        </button>
      </div>
    );
  }
  
  return (
    <div className={styles.contractList}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('contract_management')}</h1>
        <button className={styles.createButton} onClick={onCreateContract}>
          <FontAwesomeIcon icon={faPlus} />
          <span>{t('create_contract')}</span>
        </button>
      </div>
      
      <div className={styles.controls}>
        <div className={styles.searchContainer}>
          <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder={t('search_contracts')}
            value={searchTerm}
            onChange={handleSearch}
          />
          {searchTerm && (
            <button className={styles.clearSearchButton} onClick={clearSearch}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
        
        <button 
          className={`${styles.filterButton} ${showFilters ? styles.active : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FontAwesomeIcon icon={faFilter} />
          <span>{t('filters')}</span>
        </button>
      </div>
      
      {showFilters && (
        <div className={styles.filtersContainer}>
          <div className={styles.filterGroup}>
            <label htmlFor="status-dropdown">{t('status')}</label>
            <Dropdown
              id="status-dropdown"
              options={statusOptions}
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              variant="standalone"
              placeholderKey="all_statuses"
            />
          </div>
          
          <div className={styles.filterGroup}>
            <label htmlFor="contractType-dropdown">{t('contract_type')}</label>
            <Dropdown
              id="contractType-dropdown"
              options={contractTypeOptions}
              value={filters.contractType}
              onChange={(value) => handleFilterChange('contractType', value)}
              variant="standalone"
              placeholderKey="all_types"
            />
          </div>
          
          <div className={styles.filterGroup}>
            <label htmlFor="contractStructure-dropdown">{t('contract_structure')}</label>
            <Dropdown
              id="contractStructure-dropdown"
              options={contractStructureOptions}
              value={filters.contractStructure}
              onChange={(value) => handleFilterChange('contractStructure', value)}
              variant="standalone"
              placeholderKey="all_structures"
            />
          </div>
          
          <div className={styles.filterGroup}>
            <label className={styles.resetLabel}>{t('reset_all_filters')}</label>
            <button 
              className={`${styles.resetButton} ${hasActiveFilters() ? styles.active : styles.disabled}`}
              onClick={resetFilters}
              disabled={!hasActiveFilters()}
            >
              <FontAwesomeIcon icon={faUndo} className={styles.resetIcon} />
              <span>{t('reset_filters')}</span>
            </button>
          </div>
        </div>
      )}
      
      <div className={styles.tableWrapper}>
        <ReinsuranceTable
          columns={columns}
          data={filteredContracts}
          onRowClick={(row) => onViewContract(row.contract_id)}
          emptyMessage="no_contracts_found"
          sortConfig={sortConfig}
          onSort={handleSort}
          isLoading={isLoading}
          pageSize={3} // Smaller page size to ensure pagination shows
        />
      </div>
    </div>
  );
};

export default ContractList;